import { useLocation } from 'react-router-dom';
import { Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { IntegrationCardSkeleton } from '@/components/integrations/IntegrationCard';

const TABS = [
  { value: 'all', label: 'All' },
  { value: 'connected', label: 'Connected' },
  { value: 'unconnected', label: 'Not connected' },
] as const;

export function IntegrationsPageSkeleton({ cards = 6 }: { cards?: number }) {
  const location = useLocation();
  const requested = new URLSearchParams(location.search).get('tab');
  const activeTab = TABS.some((t) => t.value === requested) ? requested : 'all';

  return (
    <div className="flex min-h-full flex-col bg-white" aria-busy="true" aria-label="Loading integrations">
      <div className="border-b border-[#EAECF0] bg-white px-[15px] pt-3">
        <div className="flex items-end gap-1 overflow-x-auto overflow-y-hidden">
          {TABS.map((tab) => {
            const active = activeTab === tab.value;
            return (
              <span
                key={tab.value}
                className={cn(
                  'relative inline-flex items-center gap-2 whitespace-nowrap px-3 pb-3 pt-2 text-[13.5px] font-semibold',
                  active ? 'text-[#101828]' : 'text-[#6B7280]',
                )}
              >
                {tab.label}
                {active ? (
                  <span aria-hidden className="absolute bottom-[-1px] left-3 right-3 h-[2px] rounded-t bg-brand" />
                ) : null}
              </span>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-[#EAECF0] bg-white px-[15px] py-3">
        <div className="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-lg border border-[#E4E7EC] bg-white px-2.5 transition-colors hover:bg-slate-50 sm:w-[260px] sm:max-w-[280px] sm:flex-none">
          <Search className="h-4 w-4 shrink-0 text-[#98A2B3]" />
          <span className="min-w-0 flex-1 truncate text-[13px] text-[#98A2B3]">Search integrations...</span>
        </div>
        <div className="h-9 w-[97px] shrink-0 animate-pulse rounded-lg border border-[#EDF1F7] bg-[#F9FAFB]" />
      </div>

      <div className="flex-1 p-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {[...Array(cards)].map((_, i) => (
            <IntegrationCardSkeleton key={i} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default IntegrationsPageSkeleton;
